import { defaultAxiosInstance } from '@core/http/config';
import { HttpError, HttpRange, HttpStatusCode, HttpTask } from '@core/http/model';
import { logSentryHttpError } from '@shared/modules/sentry/utils';
import { removeEmptyString } from '@shared/utils/string';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import * as B from 'fp-ts/boolean';
import { Lazy, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as TE from 'fp-ts/TaskEither';
import { Range, RangeCursor, RangeResult } from '@shared/modules/range';
import { Filter } from '@shared/modules/filter';

function onError<E = unknown>(err: unknown): HttpError<E> {
  const error = HttpError.fromAxiosError<E>(err as AxiosError);

  if (
    error.status >= 400 &&
    ![
      HttpStatusCode.UNAUTHORIZED,
      HttpStatusCode.FORBIDDEN,
      HttpStatusCode.NOT_FOUND,
      HttpStatusCode.CONFLICT,
    ].includes(error.status)
  ) {
    console.error(error);
    logSentryHttpError(`[http] error ${error.status} on ${O.getOrElse(() => 'unknown')(error.url)} path`, error);
  }

  return error;
}

function transformRequest<R, E>(request: Lazy<Promise<AxiosResponse<R>>>): HttpTask<R, E> {
  return pipe(
    TE.tryCatch(request, err => onError<E>(err)),
    TE.map(res => res.data),
  );
}

function get<R = unknown, E = unknown>(url: string, config?: AxiosRequestConfig): HttpTask<R, E> {
  return transformRequest(() => defaultAxiosInstance.get(url, config));
}

function getRange<R = unknown, F extends Filter = {}, E = unknown>(
  url: string,
  cursor: RangeCursor,
  filter?: F,
  config?: AxiosRequestConfig,
): HttpRange<R, F, E> {
  return pipe(
    get<RangeResult<R, F>, E>(url, {
      ...config,
      params: {
        ...config?.params,
        ...cursor,
        ...filter,
      },
    }),
    TE.map(Range.fromRangeResult),
  );
}

function post<R = unknown, E = unknown>(url: string, data?: any, config?: AxiosRequestConfig): HttpTask<R, E> {
  const dataStringToNullable = pipe(
    data instanceof FormData,
    B.fold(
      () =>
        pipe(
          O.fromNullable(data),
          O.fold(
            () => data,
            data => removeEmptyString(data),
          ),
        ),
      () => data,
    ),
  );

  return transformRequest(() => defaultAxiosInstance.post(url, dataStringToNullable, config));
}

function put<R = unknown, E = unknown>(url: string, data?: any, config?: AxiosRequestConfig): HttpTask<R, E> {
  const dataStringToNullable = pipe(
    data instanceof FormData,
    B.fold(
      () =>
        pipe(
          O.fromNullable(data),
          O.fold(
            () => data,
            data => removeEmptyString(data),
          ),
        ),
      () => data,
    ),
  );

  return transformRequest(() => defaultAxiosInstance.put(url, dataStringToNullable, config));
}

function del<R = unknown, E = unknown>(url: string, config?: AxiosRequestConfig): HttpTask<R, E> {
  return transformRequest(() => defaultAxiosInstance.delete(url, config));
}

export const httpService = {
  get,
  getRange,
  post,
  put,
  delete: del,
};
